import {
    Component,
    ChangeDetectionStrategy,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    Input,
    SimpleChanges,
    HostBinding,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { navigationButtons } from 'layout/navigation-buttons';
import { AccessCondition, AccessString, AuthService, PermissionsHelper } from 'providers/auth.service';
import { EnvironmentService } from 'providers/environment.service';
import { BehaviorSubject, combineLatest } from 'rxjs';

import { DevelopmentService } from 'shared/development/development.service';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';

@Component({
    selector: 'ce-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TopbarComponent {
    @Input() pageId: string;
    @Output() menuClicked = new EventEmitter<void>();
    @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    filteredButtons: Partial<typeof navigationButtons> = [];
    organizationName$ = new BehaviorSubject(null);
    licenseName$ = new BehaviorSubject(null);
    isSearchFieldActive$ = new BehaviorSubject(false);
    hasPermissionsToSearch$ = new BehaviorSubject(false);

    devFeaturesEnabled = false;

    sectionName: string;

    constructor(
        private auth: AuthService,
        private developmentService: DevelopmentService,
        private cdRef: ChangeDetectorRef,
        private orgService: OrganizationInfoService,
        private licenseService: LicenseInfoService,
        private readonly environmentService: EnvironmentService,
        private readonly router: Router
    ) {
        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((origin) => {
                this.themeClass = origin === 'mykempower' ? 'theme-mykempower' : 'theme-chargeye';
                this.cdRef.markForCheck();
            });

        combineLatest([
            this.auth.authenticated$,
            this.developmentService.devFeaturesEnabled$(),
            this.auth.permissions$,
            this.orgService.organizationInfo$,
            this.licenseService.licenseInfo$,
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([, devEnabled, helper, organizationInfo, license]) => {
                this.devFeaturesEnabled = devEnabled;
                this.cdRef.markForCheck();

                this.organizationName$.next(
                    organizationInfo?.impersonated?.tenantName || organizationInfo?.originalTenantName || ''
                );

                this.licenseName$.next(license?.licenseDefinition?.name || license?.licenseDefinition?.type || '');

                this.refreshButtons(helper);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.pageId) {
            if (this.pageId === 'iam') {
                this.sectionName = 'Account administration';
            } else {
                this.sectionName = '';
            }
            this.cdRef.markForCheck();
        }
    }

    navigateToHome(): void {
        if (this.themeClass === 'theme-mykempower') {
            // For MyKempower, always go to stations
            this.router.navigate(['/search/stations']);
        } else {
            // For ChargEye, go to root (which will use default routing)
            this.router.navigate(['/']);
        }
    }

    setSearchActive($event): void {
        this.isSearchFieldActive$.next($event);
    }

    private refreshButtons(helper: PermissionsHelper): void {
        this.filteredButtons = [];

        this.hasPermissionsToSearch$.next(helper?.hasAccess('fleet.chargingStation.overview.globalSearch'));

        for (const b of navigationButtons) {
            // check duplicate links
            const linkExisted = this.filteredButtons.some((_b) => _b.routerLink === b.routerLink);
            if (linkExisted) {
                continue;
            }

            // tenant id filter
            if (b.tenantIds && this.auth.profile?.tenantId && !b.tenantIds.includes(this.auth.profile.tenantId)) {
                // don't add this button, for a different tenant id
            }

            // check access
            if (b.access) {
                const condition: AccessCondition = b.accessCondition || 'AND';
                const accessString: AccessString = b.access;

                if (!helper?.hasAccess(accessString, condition)) {
                    continue;
                }
            }

            // development filter
            if (b.development && !this.devFeaturesEnabled) {
                continue;
            }

            // sys admin filter - override access filter
            if (b.sysAdminOnly && !this.auth.getPermissions().isSystemAdmin) {
                continue;
            }

            this.filteredButtons.push(b);
        }

        this.cdRef.markForCheck();
    }
}
